import React from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { Box } from "@mui/material";

import Dashboard from "./pages/Dashboard";
import VehicleTracking from "./pages/VehicleTracking";
import RoleManagement from "./pages/RoleManagement";
import Reports from "./pages/Reports";
import HistoryPlayback from "./pages/HistoryPlayback";
import Geofencing from "./pages/Geofencing";
import VideoRecording from "./pages/VideoRecording";
import NotificationManagement from "./pages/NotificationManagement";
import UserManagement from "./pages/UserManagement";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import ForgotPassword from "./pages/ForgotPassword";
import PermissionManagement from "./pages/PermissionManagement";
import Vehicles from "./pages/Vehicles";
import RouteReport from "./pages/RouteReport";
import EventReport from "./pages/EventReport";
import StopReport from "./pages/StopReport";
import SummaryReport from "./pages/SummaryReport";
import GeofencingReport from "./pages/GeofencingReport";
import IgnitionReport from "./pages/IgnitionReport";
import TripReport from "./pages/TripReport";
import AllAlerts from "./pages/AllAlerts";
import ExpiredVehicles from "./pages/ExpiredVehicles";
import AddUsers from "./pages/AddUsers";
import Plans from "./pages/Plans";
import AddDevice from "./pages/AddDevice";
import DisabledDevices from "./pages/DisabledDevices";
import GroupManagement from "./pages/GroupManagement";
import { UserProvider } from "./provider/UserProvider";
import Inventory from "./pages/Inventory";
import { ProtocolsProvider } from "./provider/ProtocolsProvider";
import { NotificationProvider } from "./context/NotificationContext";
import Settings from "./pages/Settings";
import PointsHistory from "./pages/PointsHistory";
import SupportTickets from "./pages/SupportTickets";
import PointsTransfer from "./pages/PointsTransfer";
import BulkAdd from "./pages/BulkAdd";
import ThemeManagement from "./pages/ThemeManagement";
import { loadSavedTheme } from "./utlis/themeUtils";
import Navbar from "./components/layout/Navbar";
import AddDeviceInventory from "./pages/AddDeviceInventory";
import PublicLiveTracking from "./pages/PublicLiveTracking";
import BulkAddUsers from "./pages/BulkAddUsers";
import UserDevices from "./pages/UserDevices";
import TransactionManagement from "./pages/TransactionManagement";

// Create a theme
const theme = createTheme({
  palette: {
    primary: {
      main: "#1976d2",
    },
    secondary: {
      main: "#f50057",
    },
    background: {
      default: "#f5f5f5",
    },
  },
  typography: {
    fontFamily: ["Roboto", '"Helvetica Neue"', "Arial", "sans-serif"].join(","),
  },
});

function App() {
  const isAuthenticated = localStorage.getItem("isAuthenticated") === "true";
  const [, forceUpdate] = React.useState({});

  // Load saved theme on app startup
  React.useEffect(() => {
    const loadTheme = async () => {
      try {
        await loadSavedTheme();
      } catch (error) {
        console.error("Error loading saved theme:", error);
      }
    };
    loadTheme();
  }, []);

  // Listen for theme changes and force re-render
  React.useEffect(() => {
    const handleThemeChange = () => {
      forceUpdate({});
    };

    window.addEventListener("themeChanged", handleThemeChange);
    return () => {
      window.removeEventListener("themeChanged", handleThemeChange);
    };
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <NotificationProvider>
        <UserProvider>
          <ProtocolsProvider>
            <Router>
              <Routes>
                {/* Auth routes without navbar */}
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />

                {/* Public routes without navbar */}
                <Route
                  path="/live-tracking/:encryptedData"
                  element={<PublicLiveTracking />}
                />

                {/* Main app routes with navbar */}
                <Route
                  path="/*"
                  element={
                    <Box>
                      <Navbar />
                      <Routes>
                        <Route index element={<Dashboard />} />
                        <Route path="vehicles" element={<Vehicles />} />
                        <Route path="vehicles/:filter" element={<Vehicles />} />
                        <Route path="add-device" element={<AddDevice />} />
                        <Route
                          path="add-device-inventory"
                          element={<AddDeviceInventory />}
                        />
                        <Route path="edit-device/:id" element={<AddDevice />} />
                        <Route path="inventory" element={<Inventory />} />
                        <Route
                          path="disabled-devices"
                          element={<DisabledDevices />}
                        />
                        {/* Other routes can be added here later */}
                        <Route
                          path="vehicle-tracking/:id"
                          element={<VehicleTracking />}
                        />
                        <Route
                          path="role-management"
                          element={<RoleManagement />}
                        />
                        <Route
                          path="permission-management"
                          element={<PermissionManagement />}
                        />
                        <Route path="reports" element={<Reports />} />
                        <Route path="reports/route" element={<RouteReport />} />
                        <Route path="reports/event" element={<EventReport />} />
                        <Route path="reports/stop" element={<StopReport />} />
                        <Route
                          path="reports/summary"
                          element={<SummaryReport />}
                        />
                        <Route
                          path="reports/geofencing"
                          element={<GeofencingReport />}
                        />
                        <Route
                          path="reports/ignition"
                          element={<IgnitionReport />}
                        />
                        <Route path="reports/trip" element={<TripReport />} />
                        <Route path="settings" element={<Settings />} />
                        <Route
                          path="theme-management"
                          element={<ThemeManagement />}
                        />
                        <Route
                          path="points-history"
                          element={<PointsHistory />}
                        />
                        <Route
                          path="support-tickets"
                          element={<SupportTickets />}
                        />
                        <Route path="all-alerts" element={<AllAlerts />} />
                        <Route
                          path="expired-vehicles"
                          element={<ExpiredVehicles />}
                        />
                        {/* <Route path="users" element={<AddUsers />} /> */}
                        <Route path="plans" element={<Plans />} />
                        <Route
                          path="history-playback/:id"
                          element={<HistoryPlayback />}
                        />
                        <Route path="geofencing" element={<Geofencing />} />
                        <Route
                          path="video-recording"
                          element={<VideoRecording />}
                        />
                        <Route
                          path="notifications"
                          element={<NotificationManagement />}
                        />
                        <Route path="users" element={<UserManagement />} />
                        <Route
                          path="user-devices/:userId"
                          element={<UserDevices />}
                        />
                        <Route path="groups" element={<GroupManagement />} />
                        <Route
                          path="points-transfer"
                          element={<PointsTransfer />}
                        />
                        <Route path="bulk-add" element={<BulkAdd />} />
                        <Route
                          path="bulk-add-users"
                          element={<BulkAddUsers />}
                        />
                        <Route
                          path="transaction-management"
                          element={<TransactionManagement />}
                        />
                      </Routes>
                    </Box>
                  }
                />
              </Routes>
            </Router>
          </ProtocolsProvider>
        </UserProvider>
      </NotificationProvider>
    </ThemeProvider>
  );
}

export default App;
